import AccountVerificationStorage, {
    type PersistedAccountVerificationState
} from './AccountVerificationStorage';

const BASE_COOLDOWN_MS = 60 * 1000;
const COOLDOWN_BACKOFF_THRESHOLD = 3;
const COOLDOWN_BACKOFF_FACTOR = 2;
const COOLDOWN_TICK_MS = 1000;
const SUCCESS_COUNT_RESET_MS = 24 * 60 * 60 * 1000;

type CooldownListener = () => void;

export default class AccountVerificationCooldownManager {
    #successCount = 0;

    #lastSuccessAt: number | null = null;

    #cooldownEndsAt: number | null = null;

    #cooldownIntervalId: ReturnType<typeof setInterval> | null = null;

    readonly #onTick: CooldownListener;

    readonly #onEnd: CooldownListener;

    constructor(onTick: CooldownListener, onEnd: CooldownListener) {
        this.#onTick = onTick;
        this.#onEnd = onEnd;
    }

    public destroy(): void {
        this.#clearCooldownInterval();
    }

    public isOnCooldown(): boolean {
        return this.#cooldownEndsAt !== null && Date.now() < this.#cooldownEndsAt;
    }

    public getRemainingSeconds(): number {
        if (this.#cooldownEndsAt === null) {
            return 0;
        }

        return Math.max(0, Math.ceil((this.#cooldownEndsAt - Date.now()) / 1000));
    }

    public async restore(): Promise<void> {
        const persistedState = await this.#readPersistedState();

        if (persistedState === null) {
            return;
        }

        const normalizedState = this.#normalizeSuccessCount(persistedState);

        this.#successCount = normalizedState.successCount;
        this.#lastSuccessAt = normalizedState.lastSuccessAt;

        if (normalizedState.wasReset) {
            this.#persistState();
        }

        if (persistedState.cooldownEndsAt !== null && persistedState.cooldownEndsAt > Date.now()) {
            this.#cooldownEndsAt = persistedState.cooldownEndsAt;
            this.#startCooldownCountdown();
            this.#onTick();
        }
    }

    public recordSuccess(): void {
        if (this.#isSuccessCountExpired(this.#lastSuccessAt, this.#successCount)) {
            this.#successCount = 0;
            this.#lastSuccessAt = null;
        }

        this.#successCount += 1;
        this.#lastSuccessAt = Date.now();
        this.#startCooldown();
    }

    #startCooldown(): void {
        const duration = this.#calculateCooldownMs();

        this.#cooldownEndsAt = Date.now() + duration;
        this.#persistState();
        this.#startCooldownCountdown();
        this.#onTick();
    }

    #calculateCooldownMs(): number {
        const tier = Math.floor((this.#successCount - 1) / COOLDOWN_BACKOFF_THRESHOLD);

        return BASE_COOLDOWN_MS * COOLDOWN_BACKOFF_FACTOR ** tier;
    }

    #normalizeSuccessCount(
        state: PersistedAccountVerificationState
    ): PersistedAccountVerificationState & {
        wasReset: boolean;
    } {
        if (!this.#isSuccessCountExpired(state.lastSuccessAt, state.successCount)) {
            return { ...state, wasReset: false };
        }

        return {
            cooldownEndsAt: state.cooldownEndsAt,
            successCount: 0,
            lastSuccessAt: null,
            wasReset: true
        };
    }

    #isSuccessCountExpired(lastSuccessAt: number | null, successCount: number): boolean {
        if (successCount === 0) {
            return false;
        }

        if (lastSuccessAt === null) {
            return true;
        }

        return Date.now() - lastSuccessAt > SUCCESS_COUNT_RESET_MS;
    }

    #startCooldownCountdown(): void {
        this.#clearCooldownInterval();

        this.#cooldownIntervalId = setInterval(() => {
            if (!this.isOnCooldown()) {
                this.#endCooldown();

                return;
            }

            this.#onTick();
        }, COOLDOWN_TICK_MS);
    }

    #endCooldown(): void {
        this.#clearCooldownInterval();
        this.#cooldownEndsAt = null;
        this.#persistState();
        this.#onEnd();
    }

    async #readPersistedState(): Promise<PersistedAccountVerificationState | null> {
        const parsedState = await AccountVerificationStorage.getInstance().getState();

        if (parsedState === null) {
            return null;
        }

        if (
            typeof parsedState.successCount !== 'number' ||
            !Number.isFinite(parsedState.successCount) ||
            parsedState.successCount < 0
        ) {
            return null;
        }

        const cooldownEndsAt =
            typeof parsedState.cooldownEndsAt === 'number' &&
            Number.isFinite(parsedState.cooldownEndsAt)
                ? parsedState.cooldownEndsAt
                : null;

        const lastSuccessAt =
            typeof parsedState.lastSuccessAt === 'number' &&
            Number.isFinite(parsedState.lastSuccessAt)
                ? parsedState.lastSuccessAt
                : null;

        return {
            cooldownEndsAt,
            successCount: parsedState.successCount,
            lastSuccessAt
        };
    }

    #persistState(): void {
        const state: PersistedAccountVerificationState = {
            cooldownEndsAt: this.#cooldownEndsAt,
            successCount: this.#successCount,
            lastSuccessAt: this.#lastSuccessAt
        };

        if (state.cooldownEndsAt === null && state.successCount === 0) {
            AccountVerificationStorage.getInstance()
                .clear()
                .catch(() => undefined);

            return;
        }

        AccountVerificationStorage.getInstance()
            .saveState(state)
            .catch(() => undefined);
    }

    #clearCooldownInterval(): void {
        if (this.#cooldownIntervalId !== null) {
            clearInterval(this.#cooldownIntervalId);
            this.#cooldownIntervalId = null;
        }
    }
}
